import { Dialog, DialogContent, DialogTrigger } from "../../../shared/components/universal/dialog"
import React, { useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"

import IconAction from "../../../shared/components/universal/icon-action"
import Input from "../../../shared/components/fields/input"
import ReactMarkdown from "react-markdown"
import { cn } from "../../../shared/utils/cn"
import { errorToast } from "../../../shared/utils/error-toast"
import { scrollTo } from "../../../shared/utils/smooth-scroll"
import { usePostContact } from "../../../hooks/contact/use-query-contact"
import { useTranslation } from "react-i18next"

type FormValues = {
    firstname: string
    lastname: string
    email: string
    orderNumber: string
    additionalInfo: string
}

const CancellationPolicyForm = () => {
    const { t } = useTranslation("translations")

    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        if (submitted) {
            scrollTo("success_message")
        }
    }, [submitted])

    const {
        formState: { errors },
        handleSubmit,
        reset,
        control,
    } = useForm<FormValues>({
        mode: "onTouched",
        defaultValues: {
            firstname: "",
            lastname: "",
            email: "",
            orderNumber: "",
            additionalInfo: "",
        },
        context: undefined,
        criteriaMode: "firstError",
        shouldFocusError: true,
    })

    const mutate = usePostContact()

    const submit: SubmitHandler<FormValues> = async values => {
        const additionalInfo = values.additionalInfo.trim()
        const body = {
            category_id: process.env.CONTACT_CANCELLATION_CATEGORY_ID,
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            body:
                t("cancellation_policy_form_submit_body", {
                    orderNumber: values.orderNumber,
                    email: values.email,
                    firstname: values.firstname,
                    lastname: values.lastname,
                }) +
                (additionalInfo ? t("cancellation_policy_form_submit_body_additional_info", { additionalInfo }) : ""),
        }

        try {
            // @ts-ignore
            await mutate.mutateAsync(body)
            setSubmitted(true)
            reset()
        } catch (error) {
            errorToast(error)
        }
    }

    return (
        <div className="mx-auto max-w-[550px]">
            <h1
                className="mb-7 text-center font-gibson text-xl font-semibold sm:text-3xl/3xl lg:text-5xl/5xl"
                id="success_message"
            >
                {t("cancellation_policy_form_headline")}
            </h1>
            <div className="grid grid-cols-1 grid-rows-1 rounded-base bg-white p-7 sm:px-16 sm:py-10">
                <form
                    onSubmit={handleSubmit(submit)}
                    className={cn("col-span-1 col-start-1 row-span-1 row-start-1", {
                        "pointer-events-none opacity-0": submitted,
                    })}
                >
                    <Input
                        control={control}
                        rules={{ required: { value: true, message: t("form_error_required") } }}
                        type="text"
                        name="firstname"
                        label={t("cancellation_policy_form_firstname_label")}
                        autocomplete="given-name"
                        required
                    />
                    <Input
                        control={control}
                        rules={{ required: { value: true, message: t("form_error_required") } }}
                        type="text"
                        name="lastname"
                        label={t("cancellation_policy_form_lastname_label")}
                        autocomplete="family-name"
                        required
                    />
                    <Input
                        control={control}
                        rules={{ required: { value: true, message: t("form_error_required") } }}
                        type="email"
                        name="email"
                        label={t("cancellation_policy_form_email_label")}
                        autocomplete="email"
                        required
                    />
                    <Input
                        control={control}
                        rules={{ required: { value: true, message: t("form_error_required") } }}
                        type="text"
                        name="orderNumber"
                        label={t("cancellation_policy_form_order_number_label")}
                        required
                    />
                    <Dialog>
                        <DialogTrigger>
                            <div className="mb-4 flex items-center gap-2 text-blue">
                                <i className="lavita-icon text-base leading-none"></i>
                                <span className="text-left text-base font-semibold underline decoration-1 underline-offset-1">
                                    {t("cancellation_policy_form_order_number_info_button")}
                                </span>
                            </div>
                        </DialogTrigger>
                        <DialogContent>
                            <div className="sm:max-w-[472px]">
                                <h2 className="mb-4 font-gibson text-lg sm:mb-5 sm:text-xl lg:text-2xl">
                                    {t("cancellation_policy_form_order_number_label")}
                                </h2>
                                <p className="text-base sm:text-md lg:text-lg">
                                    {t("cancellation_policy_form_order_number_info")}
                                </p>
                            </div>
                        </DialogContent>
                    </Dialog>
                    <Input
                        control={control}
                        rules={{ required: false }}
                        type="text"
                        name="additionalInfo"
                        label={t("cancellation_policy_form_additional_info_label")}
                    />
                    <p className="mb-4 text-xs text-lighter-grey sm:text-sm">
                        {t("cancellation_policy_form_additional_info_hint")}
                    </p>
                    <p className="mb-7 text-xs sm:text-sm">
                        <span className="text-ui-red">*</span> {t("form_required_field")}
                    </p>
                    <div className="mb-10 sm:mb-16">
                        <IconAction
                            title={t("cancellation_policy_form_submit")}
                            button
                            submit
                            disabled={Object.keys(errors).length > 0}
                            fullWidth
                        />
                    </div>
                    <div className="flex items-center justify-center gap-2.5">
                        <i className="lavita-icon text-sm leading-none"></i>
                        <span className="text-sm">{t("cancellation_policy_form_phone_number_label")} </span>
                        <a
                            className="text-sm font-semibold text-blue underline"
                            href={`tel:${t("cancellation_policy_form_phone_url")}`}
                        >
                            {t("cancellation_policy_form_phone_number")}
                        </a>
                    </div>
                </form>
                <div
                    className={cn(
                        "pointer-events-none col-span-1 col-start-1 row-span-1 row-start-1 hidden flex-col items-center justify-center bg-white text-center sm:px-4",
                        {
                            "pointer-events-auto flex": submitted,
                        }
                    )}
                >
                    <div className="mb-5 flex h-[50px] w-[50px] items-center justify-center rounded-full bg-swamp-green sm:h-15 sm:w-15 lg:h-[70px] lg:w-[70px]">
                        <i className="lavita-icon text-lg leading-none text-white sm:text-xl"></i>
                    </div>
                    <h2 className="mb-5 font-gibson text-lg font-semibold sm:text-xl lg:text-2xl">
                        {t("cancellation_policy_form_success_headline")}
                    </h2>
                    <ReactMarkdown
                        components={{
                            p: ({ children }) => <p className="mb-7 text-base lg:text-md">{children}</p>,
                        }}
                    >
                        {t("cancellation_policy_form_success_subheadline")}
                    </ReactMarkdown>
                    <div className="mb-10">
                        <IconAction button title={t("cancellation_policy_form_success_button")} to="/" />
                    </div>
                    <ReactMarkdown
                        components={{
                            p: ({ children }) => (
                                <p className="mb-7 whitespace-pre-wrap text-base lg:text-md">{children}</p>
                            ),
                        }}
                    >
                        {t("cancellation_policy_form_success_message")}
                    </ReactMarkdown>
                    <a
                        className="mb-5 flex items-center justify-center gap-2.5 rounded-full border border-solid border-border-tide px-7 py-3.5 text-blue"
                        href={`tel:${t("cancellation_policy_form_phone_url")}`}
                    >
                        <i className="lavita-icon text-base leading-none sm:text-md"></i>
                        <span className="text-base font-semibold underline sm:text-md">
                            {t("cancellation_policy_form_phone_number")}
                        </span>
                    </a>
                    <a
                        className="flex items-center justify-center gap-2.5 rounded-full border border-solid border-border-tide px-7 py-3.5 text-blue"
                        href={`mailto:${t("cancellation_policy_form_email")}`}
                    >
                        <i className="lavita-icon text-base leading-none sm:text-md"></i>
                        <span className="text-base font-semibold underline sm:text-md">
                            {t("cancellation_policy_form_email")}
                        </span>
                    </a>
                </div>
            </div>
        </div>
    )
}

export default CancellationPolicyForm
